import parseQueryString from '../../_utils/parse-query-string';
import SizeHelpers from '../common/size-helpers';

// Compares two finish names case-insensitively, safe against missing names.
const finishNamesMatch = (a, b) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/**
 * Generates a map of available finishes for each delivery option.
 * @param {Array} finishes - All available finishes.
 * @param {Array} sizes - All available sizes.
 * @returns {Object} - An object where keys are delivery option IDs and values are arrays of finish objects.
 */
export function generateAvailableFinishesByDeliveryOption(finishes, sizes) {
  const availableFinishes = {};
  finishes.forEach((finish) => {
    sizes.forEach((size) => {
      size.options.delivery_options.forEach((deliveryOptionId) => {
        if (finish.name === size.options.finish.name) {
          availableFinishes[deliveryOptionId] ||= [];
          if (!availableFinishes[deliveryOptionId].includes(finish)) {
            availableFinishes[deliveryOptionId].push(finish);
          }
        }
      });
    });
  });
  return availableFinishes;
}

/**
 * Determines the initial finish, delivery option, and size based on query string
 *  parameters and available product configuration.
 * @param {Object} configData - The raw config data containing finishes, sizes, and delivery_options.
 * @param {Object} availableFinishesByDeliveryOption - The map generated by generateAvailableFinishesByDeliveryOption.
 * @returns {Object} - An object containing initialFinish, initialDeliveryOption, defaultSize, and viewingSizeKey.
 */
export function determineInitialSelections(
  configData,
  availableFinishesByDeliveryOption,
  photos = [],
  features = {},
) {
  const {
    size: defaultSizeParam,
    finish: defaultFinishParam,
    delivery: defaultDeliveryOptionParam,
  } = parseQueryString();

  let initialFinish;
  let initialDeliveryOption;
  let defaultSize;
  let deliveryParam;

  let initialFinishFromUrl;
  if (defaultFinishParam) {
    initialFinishFromUrl = configData.finishes.find(
      (f) => f.title.toLowerCase() === defaultFinishParam.toLowerCase(),
    );
  }

  // If MPF is enabled then set initialFinish based to URL param
  if (features.allowMultiplePrintFinishesPerOrder && initialFinishFromUrl) {
    initialFinish = initialFinishFromUrl;
  }

  // Set initialDeliveryOption and potentially initialFinish based on photos
  if (photos.length > 0) {
    const firstPhoto = photos[0];
    // Always set delivery option from first photo if photos exist
    initialDeliveryOption = configData.delivery_options.find(
      (d) => d.key === firstPhoto.delivery,
    );
    // MPF off so set finish to that of the first photo
    if (!initialFinish) {
      initialFinish = configData.finishes.find(
        (f) => f.name === firstPhoto.finish,
      );
    }
  } else if (!initialFinish && initialFinishFromUrl) {
    // No photos & finish not set yet, so use from URL
    initialFinish = initialFinishFromUrl;
  }

  if (defaultSizeParam) {
    if (initialFinish) {
      defaultSize = configData.sizes.find(
        (s) =>
          s.size_id.toLowerCase() === defaultSizeParam.toLowerCase() &&
          finishNamesMatch(s.options.finish?.name, initialFinish.name),
      );
    } else {
      defaultSize = configData.sizes.find(
        (s) => s.size_id.toLowerCase() === defaultSizeParam.toLowerCase(),
      );
    }
  }

  if (initialFinish?.name && !defaultSize) {
    defaultSize = configData.sizes.find(({ options }) =>
      finishNamesMatch(options.finish?.name, initialFinish.name),
    );
  }

  if (!defaultSize) {
    defaultSize =
      SizeHelpers.defaultSize(configData.sizes) || configData.sizes[0];
  }

  if (photos.length === 0) {
    deliveryParam = (defaultDeliveryOptionParam || '').toLowerCase();
    if (!deliveryParam) {
      deliveryParam = configData.delivery_options[0].key;
    }
    deliveryParam = parseInt(deliveryParam, 10);

    if (
      initialFinish &&
      finishNamesMatch(defaultSize.options.finish?.name, initialFinish.name) &&
      defaultSize.options.delivery_options &&
      !defaultSize.options.delivery_options.includes(deliveryParam)
    ) {
      [deliveryParam] = defaultSize.options.delivery_options;
    }

    initialDeliveryOption = configData.delivery_options.find(
      (d) => d.key === deliveryParam,
    );
    initialDeliveryOption ||= configData.delivery_options[0];
  }

  let count = 1;
  while (!initialFinish) {
    const availableFinishes =
      availableFinishesByDeliveryOption[initialDeliveryOption.key];
    if (availableFinishes) {
      [initialFinish] = availableFinishes;
    } else {
      const deliveryOptionIndex = configData.delivery_options.indexOf(
        initialDeliveryOption,
      );
      initialDeliveryOption =
        configData.delivery_options[
          (deliveryOptionIndex + 1) % configData.delivery_options.length
        ];

      if (count > configData.delivery_options.length) {
        console.error('Could not find a valid finish/delivery combo.');
        // If no combo is found after trying all delivery options,
        // fall back to the very first available delivery option and its first finish.
        const suggestedDeliveryOption = configData.delivery_options[0];
        const suggestedFinishes =
          availableFinishesByDeliveryOption[suggestedDeliveryOption.key];
        const suggestedFinish = suggestedFinishes ? suggestedFinishes[0] : null;

        return {
          error: true, // trigger invalid delivery option modal
          nextDeliveryOption: suggestedDeliveryOption,
          printFinish: suggestedFinish,
        };
      }
      count += 1;
    }
  }

  // Note: this will only be applied on mobile
  const viewingSizeKey =
    (defaultSizeParam && defaultSize.key) || SizeHelpers.ALL_PHOTOS_KEY;

  // Print size for new uploads
  const initialUploadSizeKey = defaultSize.key;

  return {
    initialFinish,
    initialDeliveryOption,
    viewingSizeKey,
    initialUploadSizeKey,
  };
}
